import { useEffect, useRef, useState, MouseEvent, KeyboardEvent, RefObject } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import isEmpty from 'lodash-es/isEmpty';
import { AlertTriangleIcon } from 'lucide-react';
import { useForm, Controller, FieldValues, useWatch } from 'react-hook-form';
import { AnyObject, ObjectSchema } from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import { getBorderColor, getHoverBorderColor, selectAllText } from './helper';

const variantClasses = {
  'body-regular': 'text-sm leading-5 font-normal',
  'body-semi-bold': 'text-sm leading-5 font-semibold',
  'label-caps-large': 'text-sm leading-5 font-medium',
  '!label-regular': '!text-sm !leading-5 !font-normal',
  '!subheading-semi-bold': '!text-base !leading-6 !font-semibold',
  '!subtitle1': '!text-base !leading-6 !font-semibold',
  '!h2-semi-bold': '!text-xl !leading-7 !font-semibold',
} as const;

export type EditableTextVariant = keyof typeof variantClasses;

type EditableTextProps<T = Record<string, unknown>> = {
  defaultValue: string;
  canEdit?: boolean;
  variant?: EditableTextVariant;
  color?: string;
  onSubmit: (text: string) => void;
  className?: string;
  validationSchema?: T;
  overflowEllipsis?: boolean;
  startFromEditMode?: boolean;
  canClear?: boolean;
  placeholder?: string;
  customSanitizer?: (value: string) => string;
  maxWidth?: number;
  minWidth?: number;
  containerRef?: RefObject<HTMLElement | null>;
};

export const EditableText = <T,>({
  defaultValue,
  canEdit = true,
  variant = '!label-regular',
  onSubmit,
  className,
  validationSchema,
  overflowEllipsis = false,
  startFromEditMode = false,
  canClear = false,
  color = 'var(--foreground)',
  placeholder,
  customSanitizer,
  maxWidth,
  minWidth,
  containerRef,
}: EditableTextProps<T>) => {
  // Local state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [exceedsMaxWidth, setExceedsMaxWidth] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Hooks
  const {
    control,
    reset,
    formState: { errors },
    setValue,
    trigger,
    clearErrors,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { inputField: defaultValue ?? '' },
    resolver: validationSchema
      ? yupResolver(validationSchema as ObjectSchema<FieldValues, AnyObject, unknown, ''>)
      : undefined,
  });

  // Refs
  const inputRef = useRef(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  // Local variables
  const hasError = !isEmpty(errors);
  const watchValue = useWatch({ control, name: 'inputField' });
  const borderColor = isHovering
    ? getHoverBorderColor(hasError, isEditMode, canEdit)
    : getBorderColor(hasError, isEditMode, canEdit);
  const showPlaceholder = !isEditMode && !!placeholder && !watchValue && !hasError;

  // Build input className based on state
  const inputClassName = cn(
    'h-9 rounded-md border bg-transparent px-1 py-0.5 text-left transition-colors duration-200 hover:bg-muted',
    variantClasses[variant],
    overflowEllipsis && 'max-w-[170px]',
    overflowEllipsis && !isEditMode && exceedsMaxWidth && 'truncate',
    !overflowEllipsis && !maxWidth && !containerRef && 'w-content',
    // In edit mode, let the input fill the available row width instead of hugging text.
    isEditMode && !overflowEllipsis && 'w-full'
  );

  const inputStyle = {
    color,
    borderColor,
    maxWidth: maxWidth != null ? `${maxWidth}px` : undefined,
    minWidth: minWidth != null ? `${minWidth}px` : undefined,
  };

  // Event handlers
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    preventClickThrough(event);
    setIsEditMode(true);
  };

  const handleDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
    preventClickThrough(event);

    if (!canEdit) {
      return;
    }

    setIsEditMode(true);
    selectAllText(event.target as HTMLInputElement);
  };

  const handleBlur = async (value: string, didChange: boolean) => {
    if (!canEdit) {
      return;
    }

    setIsEditMode(false);

    // Click-in/click-out without editing shouldn't surface validation errors. Clear any
    // stale error from a prior interaction and bail before validating/submitting.
    if (!didChange) {
      clearErrors('inputField');
      return;
    }

    const trimmedValue = value?.trim();

    // Clearable fields treat an emptied value as a deletion — submit '' and skip validation.
    if (!trimmedValue && canClear) {
      clearErrors('inputField');
      onSubmit('');
      return;
    }

    const isValid = await trigger('inputField');

    if (isValid) {
      onSubmit(trimmedValue);
    }
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLInputElement) {
      preventClickThrough(e);

      if (isKeyEnter(e)) {
        setIsEditMode(true);
        e.target.blur();
        return;
      }

      if (e.key === 'Escape') {
        // Passes the escape key press to the onBlur handler
        e.target.setAttribute('data-escape-pressed', 'true');

        setIsEditMode(false);
        e.target.blur();
      }
    }
  };

  useEffect(() => {
    reset({ inputField: defaultValue });
  }, [defaultValue, reset, trigger]);

  // Re-measure after web fonts load — async font (no font-display) measures too narrow at first.
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) {
        setFontsLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Updates the width of the input field to match the width of the text
  useEffect(() => {
    if (isEditMode) {
      // While editing, clear the inline width so the input can flex to fill its row
      // (Tailwind `w-full` on the input handles the visual width).
      inputRef.current?.style.removeProperty('width');
      return;
    }

    if (!overflowEllipsis || !isEditMode) {
      // Dynamic width calculation for view mode or when ellipsis is disabled
      if (spanRef.current && inputRef.current) {
        let width: number;
        if (inputRef.current.scrollWidth > inputRef.current.offsetWidth) {
          width = inputRef.current.scrollWidth;
        } else {
          width = spanRef.current.offsetWidth;
        }
        const containerWidth = containerRef?.current?.offsetWidth;
        if (containerWidth != null) {
          width = Math.min(width, containerWidth);
        }
        inputRef.current.style.width = `${width}px`;
      }
    }
  }, [watchValue, overflowEllipsis, isEditMode, containerRef, fontsLoaded]);

  // Check if element exceeds 170px to determine if truncate should be applied
  useEffect(() => {
    if (overflowEllipsis && spanRef.current) {
      const width = spanRef.current.offsetWidth;
      setExceedsMaxWidth(width > 170);
    }
  }, [watchValue, overflowEllipsis]);

  // Sets the input field to be in edit mode when the component is first rendered
  useEffect(() => {
    if (startFromEditMode && inputRef) {
      setIsEditMode(true);
      inputRef.current.focus();
      selectAllText(inputRef.current);
    }
  }, [startFromEditMode]);

  const getSanitizedValue = (value: string) => {
    if (customSanitizer) {
      return customSanitizer(value);
    }

    // Custom logic to prevent multiple spaces from being entered
    return value.replace(/\s{2,}/g, ' ');
  };

  return (
    <div
      className={cn(
        'relative flex items-center gap-1',
        // While editing, take the available row width so the input can expand.
        isEditMode && 'min-w-0 flex-1',
        className
      )}
    >
      <span
        className={cn(
          'absolute top-0 left-0 -z-1 min-w-5 rounded-md border border-transparent bg-transparent px-1 py-0.5 text-left whitespace-nowrap text-transparent',
          variantClasses[variant]
        )}
        ref={spanRef}
      >
        {showPlaceholder ? placeholder : watchValue}
      </span>

      <Controller
        name="inputField"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            ref={inputRef}
            type="text"
            placeholder={showPlaceholder ? placeholder : undefined}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            onKeyUp={handleKeyUp}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onChange={(e) => {
              const sanitizedValue = getSanitizedValue(e.target.value);
              setValue('inputField', sanitizedValue);
            }}
            onBlur={(e) => {
              // Prevents the onBlur handler from being called when the escape key is pressed
              if (e.target.getAttribute('data-escape-pressed') === 'true') {
                e.target.removeAttribute('data-escape-pressed');
                reset({ inputField: defaultValue });
                return;
              }

              // Prevents the onBlur handler from being called when the user
              // navigates away from the input field without entered editing mode
              if (isEditMode) {
                const trimmedValue = field.value?.trim();
                const didChange = trimmedValue !== (defaultValue ?? '').trim();
                // Only mark the field as blurred to RHF (which auto-validates under
                // mode='onBlur') when there's a non-empty new value to validate.
                // Skipping the call covers: unchanged value, and clearing the field.
                if (didChange && trimmedValue) {
                  field.onBlur();
                }
                handleBlur(field.value, didChange);
              }
            }}
            className={inputClassName}
            style={inputStyle}
            readOnly={!isEditMode || !canEdit}
            autoFocus={startFromEditMode}
            aria-invalid={!!errors?.inputField}
          />
        )}
      />

      {hasError && (
        <InfoTooltip
          content={errors?.inputField?.message as string}
          showArrow
          side="top"
          data-testid="error-tooltip"
        >
          <AlertTriangleIcon size={16} color="var(--destructive)" />
        </InfoTooltip>
      )}
    </div>
  );
};
